import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import AuditTrailsToolbar from "./Toolbar";
import { getAudit } from "@services/audit";
import { EntityTypeList, getEntityTypeArray } from "./const";
import ListView from "@common/components/ListDetailViews/ListView";
import { useErrorNotification } from "@common/components/Notification";
import { WithGrow } from "@common/components/WithGrow";
import { TabValue } from "@pages/Assets/common/FilterColumn/context";
import { AuditListContext, AuditProvider } from "./context";
import TopBanner from "@common/components/TopBanner";
import { getColumns } from "./ColDef";
import {
  FiltersTypeImpl,
  areLocalStorageFiltersApplicable,
  useOtherFilters,
  useUserFilter,
} from "@common/utils/filters";
import mappers from "@common/utils/filters/mappers";
import { useLocation } from "react-router-dom";
import { useGlobalListPreferences } from "@common/components/GlobalPreferences/hooks";
import { SpecificPreferencesProvider } from "@common/components/SpecificPreferences";
import { ListViewPreferenceKey } from "@services/userPreferences/types";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import {
  attachPreferenceFunctions,
  sortColumnOrder,
} from "@common/components/SpecificPreferences/utils";

export type AuditProps = {
  entityType?: EntityTypeList;
  entityId?: string;
  userIds?: string[];
  action?: string;
  accounts?: string[];
  accountTimePeriod?: boolean;
};

const WithNavBar = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();

  return (
    <WithGrow
      direction="column"
      fixed={<TopBanner title={t("Audit Trails")} />}
    >
      {children}
    </WithGrow>
  );
};

const CONFIGURATION_PATH = "/configuration/audits";
const Audit = (props: AuditProps) => {
  const { entityType = "", entityId = "", userIds, accounts = [] } = props;
  const [auditFilters] = useOtherFilters("AuditTrails");
  const [{ pageSize }] = useGlobalListPreferences();
  const {
    dispatch,
    list,
    total,
    page = 1,
    sort,
  } = useContext(AuditListContext);
  const dispatchError = useErrorNotification();
  const [
    { columnOrder, columnWidths },
    setSpecificListPreferences,
    { updateWidth },
  ] = useSpecificListPreferences();

  const { pathname } = useLocation();

  const { t } = useTranslation();

  const index = (page - 1) * pageSize;
  const refetch = useCallback(async () => {
    try {
      const filters = mappers.AuditTrails.fromDefinition.toSL(auditFilters);
      const entityTypeArray = getEntityTypeArray(filters, entityType);
      const audits = await getAudit(
        {
          ...filters,
          accountIds:
            entityType !== "ACCOUNT"
              ? filters.accountIds ?? accounts
              : undefined,
          entityType: entityTypeArray,
          entityId: entityId ? [entityId] : undefined,
          userIds: userIds,
        },
        {
          maxResults: pageSize,
          orderBy: `${sort.column} ${sort.direction}`,
          startIndex: index,
        }
      );
      dispatch(audits);
    } catch (e) {
      dispatchError(e);
    }
  }, [auditFilters, page, sort, accounts, pageSize]);

  useEffect(() => {
    if (list !== undefined) dispatch("reset");
  }, [accounts.join(","), sort, auditFilters, pageSize]);
  useEffect(() => {
    if (list !== undefined) {
      dispatch({ list: undefined });
    }
  }, [page]);
  useEffect(() => {
    if (list === undefined) refetch();
  }, [list]);

  const columns = useMemo(() => {
    const columns = getColumns(t).filter(
      (c) => c.tab && c.tab.indexOf(TabValue.Info) !== -1
    );
    return attachPreferenceFunctions(columns)
      .applyColumnSizes(columnWidths)
      .sortColumnOrder(columnOrder);
  }, [t, columnOrder, columnWidths]);
  return pathname === CONFIGURATION_PATH ? (
    <WithNavBar>
      <WithGrow
        direction="column"
        fixed={
          <AuditTrailsToolbar
            entityType={entityType}
            accounts={accounts}
            entityId={entityId}
            userIds={userIds}
          />
        }
      >
        <ListView
          data={list ?? []}
          columns={columns}
          onColumnsOrderChange={(order) =>
            setSpecificListPreferences({ columnOrder: order.map((c) => c.key) })
          }
          onColumnWidthChange={updateWidth}
          loading={list === undefined}
          pinFirstColumn
          total={total}
          sort={sort}
          onSort={(column, direction) =>
            dispatch({ sort: { column, direction } })
          }
          setPage={(page) => dispatch({ page })}
          pageSize={pageSize}
          page={page}
          rememberScrollTop
        />
      </WithGrow>
    </WithNavBar>
  ) : (
    <WithGrow
      direction="column"
      fixed={
        <AuditTrailsToolbar
          entityType={entityType}
          accounts={accounts}
          entityId={entityId}
          userIds={userIds}
        />
      }
    >
      <ListView
        data={list ?? []}
        columns={columns}
        onColumnsOrderChange={(order) =>
          setSpecificListPreferences({ columnOrder: order.map((c) => c.key) })
        }
        onColumnWidthChange={updateWidth}
        loading={list === undefined}
        pinFirstColumn
        total={total}
        sort={sort}
        onSort={(column, direction) =>
          dispatch({ sort: { column, direction } })
        }
        setPage={(page) => dispatch({ page })}
        pageSize={pageSize}
        page={page}
        rememberScrollTop
      />
    </WithGrow>
  );
};

const Container = (props: AuditProps) => {
  const [auditFilters] = useUserFilter("auditFilters");
  const state = (useLocation().state as any) ?? {};
  const [filters, setFilters] = useOtherFilters(FiltersTypeImpl.AuditTrails);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  useEffect(() => {
    setHasLoaded(true);
    if (
      filters.length === 0 &&
      areLocalStorageFiltersApplicable(auditFilters, state)
    ) {
      setFilters(auditFilters ?? []);
    }
  }, []);

  if (!hasLoaded) return <></>;

  return (
    <SpecificPreferencesProvider assetKey={ListViewPreferenceKey.AUDIT_TRAIL}>
      <AuditProvider>
        <Audit {...props} />
      </AuditProvider>
    </SpecificPreferencesProvider>
  );
};
export default Container;
